import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaGithub, FaLinkedin, FaTwitter } from 'react-icons/fa';

const NAV_LINKS = [
  { name: 'Home',       href: '#home',       icon: '⌂' },
  { name: 'About',      href: '#about',      icon: '◈' },
  { name: 'Skills',     href: '#skills',     icon: '⚡' },
  { name: 'Projects',   href: '#projects',   icon: '◎' },
  { name: 'Experience', href: '#experience', icon: '◆' },
  { name: 'Contact',    href: '#contact',    icon: '✦' },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [navMounted, setNavMounted] = useState(false);
  const [pillStyle, setPillStyle] = useState({});
  const navRef = useRef(null);
  const linksRef = useRef([]);

  // Nav entrance animation on mount
  useEffect(() => {
    const t = setTimeout(() => setNavMounted(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Scroll tracking
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
      const scrollY = window.scrollY + 120;
      for (const link of NAV_LINKS) {
        const id = link.href.substring(1);
        const el = document.getElementById(id);
        if (el) {
          const { offsetTop, offsetHeight } = el;
          if (scrollY >= offsetTop && scrollY < offsetTop + offsetHeight) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Floating pill indicator that follows active link
  useEffect(() => {
    const activeIndex = NAV_LINKS.findIndex(l => l.href.substring(1) === activeSection);
    const el = linksRef.current[activeIndex];
    if (el && navRef.current) {
      const navRect = navRef.current.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      setPillStyle({
        left: elRect.left - navRect.left,
        width: elRect.width,
        opacity: 1,
      });
    }
  }, [activeSection, navMounted]);

  const scrollTo = useCallback((href) => {
    const targetId = href.substring(1);
    const targetElement = document.querySelector(href);
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(targetId);
      setIsOpen(false);
    }
  }, []);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');

        :root {
          --accent:  #00f5d4;
          --accent2: #7b61ff;
          --text:    #e8eaf6;
          --muted:   rgba(232,234,246,0.45);
          --border:  rgba(0,245,212,0.14);
          --glow:    0 0 20px rgba(0,245,212,0.4);
          --surface: rgba(7,9,15,0.92);
        }

        /* ── KEYFRAMES ── */

        @keyframes navSlideDown {
          0%   { opacity: 0; transform: translateY(-24px) scaleX(0.92); filter: blur(8px); }
          60%  { opacity: 1; filter: blur(0); }
          100% { transform: translateY(0) scaleX(1); }
        }

        @keyframes logoReveal {
          0%   { opacity: 0; transform: translateX(-20px) rotate(-6deg); }
          70%  { transform: translateX(4px) rotate(1deg); }
          100% { opacity: 1; transform: translateX(0) rotate(0deg); }
        }

        @keyframes linkFadeIn {
          0%   { opacity: 0; transform: translateY(-10px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes actionsFadeIn {
          0%   { opacity: 0; transform: translateX(20px); }
          100% { opacity: 1; transform: translateX(0); }
        }

        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33%       { transform: translateY(-4px) rotate(1deg); }
          66%       { transform: translateY(-2px) rotate(-1deg); }
        }

        @keyframes logoBorderSpin {
          0%   { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes dotPulse {
          0%, 100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(0,245,212,0.7); }
          50%       { transform: scale(1.3); opacity: 0.8; box-shadow: 0 0 0 5px rgba(0,245,212,0); }
        }

        @keyframes hireShimmer {
          0%   { background-position: -200% center; }
          100% { background-position: 200% center; }
        }

        @keyframes pillGlide {
          0%   { opacity: 0; transform: scaleX(0.6); }
          100% { opacity: 1; transform: scaleX(1); }
        }

        @keyframes drawerSlide {
          0%   { opacity: 0; transform: translateY(-12px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @keyframes mobileItemIn {
          0%   { opacity: 0; transform: translateX(-18px) skewX(-4deg); }
          70%  { transform: translateX(3px) skewX(1deg); }
          100% { opacity: 1; transform: translateX(0) skewX(0deg); }
        }

        @keyframes iconSpin {
          0%   { transform: rotate(0deg) scale(1); }
          50%  { transform: rotate(180deg) scale(1.2); }
          100% { transform: rotate(360deg) scale(1); }
        }

        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0,245,212,0); }
          50%       { box-shadow: 0 0 20px 4px rgba(0,245,212,0.25); }
        }

        /* ── NAV ── */
        .sh-nav {
          position: fixed; top: 0; left: 0; right: 0;
          z-index: 1000;
          padding: 0 2rem;
          font-family: 'Syne', sans-serif;
          transition: background 0.5s cubic-bezier(0.22,1,0.36,1),
                      border-color 0.5s ease,
                      box-shadow 0.5s ease;
        }
        .sh-nav.mounted {
          animation: navSlideDown 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards;
        }
        .sh-nav.scrolled {
          background: var(--surface);
          backdrop-filter: blur(24px) saturate(1.8);
          -webkit-backdrop-filter: blur(24px) saturate(1.8);
          border-bottom: 1px solid var(--border);
          box-shadow: 0 8px 48px rgba(0,0,0,0.55), 0 1px 0 rgba(0,245,212,0.07);
        }

        /* ── INNER ── */
        .sh-inner {
          max-width: 1200px;
          margin: 0 auto;
          height: 72px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1.5rem;
        }

        /* ── LOGO ── */
        .sh-logo {
          display: flex; align-items: center; gap: 12px;
          text-decoration: none; flex-shrink: 0;
          animation: logoReveal 0.8s cubic-bezier(0.34,1.56,0.64,1) 0.15s both;
        }
        .sh-logo-mark {
          width: 44px; height: 44px;
          border-radius: 10px;
          display: flex; align-items: center; justify-content: center;
          position: relative; overflow: hidden;
          background: rgba(0,245,212,0.05);
          animation: logoFloat 4s ease-in-out infinite, glowPulse 3s ease-in-out infinite;
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .sh-logo-mark::before {
          content: '';
          position: absolute; inset: -1px;
          border-radius: 11px;
          background: conic-gradient(
            from var(--angle, 0deg),
            transparent 60%,
            rgba(0,245,212,0.7) 80%,
            rgba(123,97,255,0.6) 90%,
            transparent 100%
          );
          animation: logoBorderSpin 3s linear infinite;
        }
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        .sh-logo-mark::after {
          content: '';
          position: absolute; inset: 1px;
          border-radius: 9px;
          background: rgba(7,9,15,0.95);
        }
        .sh-logo-mark svg { position: relative; z-index: 1; }
        .sh-logo:hover .sh-logo-mark {
          transform: scale(1.1) rotate(-6deg);
        }
        .sh-logo:hover .sh-logo-mark svg {
          animation: iconSpin 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
        }
        .sh-logo-text { display: flex; flex-direction: column; line-height: 1; }
        .sh-logo-name {
          font-size: 1.3rem; font-weight: 800; letter-spacing: -0.02em;
          background: linear-gradient(90deg, #fff 20%, var(--accent) 60%, var(--accent2) 100%);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          transition: letter-spacing 0.3s ease;
        }
        .sh-logo:hover .sh-logo-name { letter-spacing: 0.01em; }
        .sh-logo-sub {
          font-family: 'Space Mono', monospace;
          font-size: 0.6rem; color: var(--accent);
          letter-spacing: 0.15em; text-transform: uppercase;
          opacity: 0.75; margin-top: 3px;
        }

        /* ── NAV LINKS WRAPPER (relative for pill) ── */
        .sh-links-wrap {
          position: relative;
          display: flex; align-items: center;
        }

        /* Gliding pill indicator */
        .sh-pill {
          position: absolute;
          bottom: 0;
          height: 100%;
          background: rgba(0,245,212,0.07);
          border-radius: 8px;
          border: 1px solid rgba(0,245,212,0.13);
          pointer-events: none;
          transition: left 0.4s cubic-bezier(0.34,1.2,0.64,1),
                      width 0.4s cubic-bezier(0.34,1.2,0.64,1),
                      opacity 0.3s ease;
          animation: pillGlide 0.5s cubic-bezier(0.34,1.56,0.64,1) both;
        }

        /* ── DESKTOP NAV LINKS ── */
        .sh-links {
          display: flex; align-items: center;
          list-style: none; gap: 0.1rem;
          margin: 0; padding: 0;
          position: relative;
        }
        .sh-links li {
          animation: linkFadeIn 0.5s ease both;
        }
        .sh-links li:nth-child(1) { animation-delay: 0.25s; }
        .sh-links li:nth-child(2) { animation-delay: 0.32s; }
        .sh-links li:nth-child(3) { animation-delay: 0.39s; }
        .sh-links li:nth-child(4) { animation-delay: 0.46s; }
        .sh-links li:nth-child(5) { animation-delay: 0.53s; }
        .sh-links li:nth-child(6) { animation-delay: 0.60s; }

        .sh-links li a {
          position: relative;
          display: flex; align-items: center; gap: 6px;
          padding: 8px 13px;
          text-decoration: none;
          font-family: 'Space Mono', monospace;
          font-size: 0.75rem; font-weight: 400;
          letter-spacing: 0.05em; text-transform: uppercase;
          color: var(--muted);
          border-radius: 8px;
          transition: color 0.25s ease, transform 0.2s ease;
        }
        .sh-links li a .sh-icon {
          font-size: 0.85rem;
          display: inline-block;
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .sh-links li a:hover {
          color: var(--text);
          transform: translateY(-1px);
        }
        .sh-links li a:hover .sh-icon {
          transform: rotate(-10deg) scale(1.25);
        }
        .sh-links li a.sh-active {
          color: var(--accent);
        }
        .sh-links li a.sh-active .sh-icon {
          transform: scale(1.2);
        }

        /* Active link underline accent */
        .sh-links li a::after {
          content: '';
          position: absolute; bottom: 4px; left: 50%;
          width: 0; height: 2px;
          background: linear-gradient(90deg, var(--accent2), var(--accent));
          border-radius: 2px;
          transform: translateX(-50%);
          transition: width 0.35s cubic-bezier(0.22,1,0.36,1);
          box-shadow: 0 0 8px rgba(0,245,212,0.6);
        }
        .sh-links li a.sh-active::after { width: 60%; }

        /* ── RIGHT ACTIONS ── */
        .sh-actions {
          display: flex; align-items: center; gap: 12px; flex-shrink: 0;
          animation: actionsFadeIn 0.6s cubic-bezier(0.34,1.56,0.64,1) 0.4s both;
        }
        .sh-socials { display: flex; align-items: center; gap: 2px; }
        .sh-social {
          width: 36px; height: 36px;
          display: flex; align-items: center; justify-content: center;
          border-radius: 8px;
          color: var(--muted);
          text-decoration: none;
          border: 1px solid transparent;
          transition: color 0.25s ease,
                      border-color 0.25s ease,
                      transform 0.3s cubic-bezier(0.34,1.56,0.64,1),
                      background 0.25s ease;
          position: relative;
        }
        .sh-social:hover {
          color: var(--accent);
          border-color: var(--border);
          transform: translateY(-3px) rotate(-5deg);
          background: rgba(0,245,212,0.06);
        }
        .sh-social:nth-child(2):hover { transform: translateY(-3px) scale(1.15); }
        .sh-social:nth-child(3):hover { transform: translateY(-3px) rotate(5deg); }

        .sh-divider {
          width: 1px; height: 26px;
          background: linear-gradient(to bottom, transparent, var(--border), transparent);
        }

        /* ── HIRE ME BTN ── */
        .sh-hire {
          position: relative;
          display: flex; align-items: center; gap: 8px;
          padding: 9px 22px;
          background: transparent;
          border: 1.5px solid rgba(0,245,212,0.6);
          border-radius: 8px;
          color: var(--accent);
          font-family: 'Space Mono', monospace;
          font-size: 0.75rem; font-weight: 700;
          letter-spacing: 0.12em; text-transform: uppercase;
          cursor: pointer; overflow: hidden;
          text-decoration: none;
          transition: color 0.3s ease,
                      border-color 0.3s ease,
                      transform 0.3s cubic-bezier(0.34,1.56,0.64,1),
                      box-shadow 0.3s ease;
          /* Shimmer bg */
          background-image: linear-gradient(
            110deg,
            transparent 25%,
            rgba(0,245,212,0.08) 50%,
            transparent 75%
          );
          background-size: 200% auto;
          animation: hireShimmer 3s linear infinite;
        }
        .sh-hire:hover {
          color: #07090f;
          background: var(--accent);
          background-image: none;
          border-color: var(--accent);
          box-shadow: 0 0 32px rgba(0,245,212,0.5), 0 4px 20px rgba(0,0,0,0.4);
          transform: translateY(-2px) scale(1.03);
        }
        .sh-hire:hover .sh-dot { background: #07090f; animation: none; }
        .sh-hire:active { transform: translateY(0) scale(0.98); }

        .sh-dot {
          width: 6px; height: 6px; border-radius: 50%;
          background: var(--accent);
          animation: dotPulse 1.5s cubic-bezier(0.45,0,0.55,1) infinite;
          flex-shrink: 0;
        }

        /* ── MOBILE TOGGLE ── */
        .sh-toggle {
          display: none;
          flex-direction: column; justify-content: center; align-items: center;
          width: 40px; height: 40px;
          background: rgba(0,245,212,0.05);
          border: 1px solid var(--border);
          border-radius: 8px; cursor: pointer; gap: 5px;
          transition: background 0.3s ease, border-color 0.3s ease, transform 0.2s ease;
        }
        .sh-toggle:hover {
          background: rgba(0,245,212,0.1);
          border-color: rgba(0,245,212,0.25);
          transform: scale(1.05);
        }
        .sh-toggle span {
          display: block; width: 20px; height: 1.5px;
          background: var(--accent); border-radius: 2px;
          transition: all 0.4s cubic-bezier(0.34,1.56,0.64,1);
          transform-origin: center;
        }
        .sh-toggle.open span:nth-child(1) { transform: rotate(45deg) translate(4.5px, 4.5px); }
        .sh-toggle.open span:nth-child(2) { opacity: 0; transform: scaleX(0) rotate(90deg); }
        .sh-toggle.open span:nth-child(3) { transform: rotate(-45deg) translate(4.5px, -4.5px); }

        /* ── MOBILE DRAWER ── */
        .sh-drawer {
          position: fixed;
          top: 72px; left: 0; right: 0;
          background: rgba(7,9,15,0.97);
          backdrop-filter: blur(28px);
          -webkit-backdrop-filter: blur(28px);
          border-bottom: 1px solid var(--border);
          max-height: 0; overflow: hidden;
          transition: max-height 0.5s cubic-bezier(0.22,1,0.36,1);
          z-index: 999;
        }
        .sh-drawer.open { max-height: 560px; }
        .sh-drawer-inner {
          padding: 1.25rem 1.5rem 1.75rem;
          display: flex; flex-direction: column; gap: 4px;
        }

        .sh-mobile-link {
          display: flex; align-items: center; gap: 14px;
          padding: 13px 16px; border-radius: 8px;
          text-decoration: none;
          font-family: 'Space Mono', monospace;
          font-size: 0.82rem; font-weight: 400;
          letter-spacing: 0.08em; text-transform: uppercase;
          color: var(--muted);
          border: 1px solid transparent;
          opacity: 0;
          transform: translateX(-18px) skewX(-4deg);
          transition: color 0.25s ease,
                      background 0.25s ease,
                      border-color 0.25s ease,
                      transform 0.2s ease;
        }
        .sh-drawer.open .sh-mobile-link {
          animation: mobileItemIn 0.5s cubic-bezier(0.34,1.56,0.64,1) forwards;
        }
        .sh-drawer.open .sh-mobile-link:nth-child(1) { animation-delay: 0.06s; }
        .sh-drawer.open .sh-mobile-link:nth-child(2) { animation-delay: 0.12s; }
        .sh-drawer.open .sh-mobile-link:nth-child(3) { animation-delay: 0.18s; }
        .sh-drawer.open .sh-mobile-link:nth-child(4) { animation-delay: 0.24s; }
        .sh-drawer.open .sh-mobile-link:nth-child(5) { animation-delay: 0.30s; }
        .sh-drawer.open .sh-mobile-link:nth-child(6) { animation-delay: 0.36s; }

        .sh-mobile-link:hover, .sh-mobile-link.sh-active {
          color: var(--accent);
          background: rgba(0,245,212,0.06);
          border-color: rgba(0,245,212,0.14);
          transform: translateX(4px);
        }

        .sh-mobile-hire {
          margin-top: 14px; padding: 14px; text-align: center;
          border: 1.5px solid var(--accent); border-radius: 8px;
          color: var(--accent); text-decoration: none;
          font-family: 'Space Mono', monospace;
          font-size: 0.78rem; font-weight: 700;
          letter-spacing: 0.12em; text-transform: uppercase;
          opacity: 0;
          transition: all 0.3s ease;
          display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .sh-drawer.open .sh-mobile-hire {
          animation: mobileItemIn 0.5s cubic-bezier(0.34,1.56,0.64,1) 0.42s forwards;
        }
        .sh-mobile-hire:hover {
          background: var(--accent);
          color: #07090f;
          box-shadow: 0 0 24px rgba(0,245,212,0.4);
          transform: scale(1.02);
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 900px) {
          .sh-links-wrap { display: none; }
          .sh-socials     { display: none; }
          .sh-divider     { display: none; }
          .sh-hire        { display: none; }
          .sh-toggle      { display: flex; }
        }
      `}</style>

      <nav className={`sh-nav${scrolled ? ' scrolled' : ''}${navMounted ? ' mounted' : ''}`}>
        <div className="sh-inner">

          {/* Logo */}
          <a href="#home" className="sh-logo" onClick={(e) => { e.preventDefault(); scrollTo('#home'); }}>
            <div className="sh-logo-mark">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{position:'relative',zIndex:1}}>
                <path d="M3 7l7-4 7 4v6l-7 4-7-4V7z" stroke="#00f5d4" strokeWidth="1.5" fill="none"/>
                <path d="M10 3v14M3 7l7 4 7-4" stroke="#00f5d4" strokeWidth="1" opacity="0.5"/>
              </svg>
            </div>
            <div className="sh-logo-text">
              <span className="sh-logo-name">Subodh</span>
              <span className="sh-logo-sub">Full Stack Dev</span>
            </div>
          </a>

          {/* Desktop Nav with gliding pill */}
          <div className="sh-links-wrap" ref={navRef}>
            <div
              className="sh-pill"
              style={pillStyle}
            />
            <ul className="sh-links">
              {NAV_LINKS.map((link, i) => (
                <li key={i}>
                  <a
                    href={link.href}
                    ref={el => linksRef.current[i] = el}
                    className={activeSection === link.href.substring(1) ? 'sh-active' : ''}
                    onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
                  >
                    <span className="sh-icon">{link.icon}</span>
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Actions */}
          <div className="sh-actions">
            <div className="sh-socials">
              <a className="sh-social" href="https://github.com/SuboKumar2356" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <FaGithub size={18} />
              </a>
              <a className="sh-social" href="https://linkedin.com/in/subokumar2356" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <FaLinkedin size={18} />
              </a>
              <a className="sh-social" href="https://twitter.com/subokumar2356" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <FaTwitter size={17} />
              </a>
            </div>

            <div className="sh-divider" />

            <a
              href="#contact"
              className="sh-hire"
              onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }}
            >
              <span className="sh-dot" />
              Hire Me
            </a>

            <button
              className={`sh-toggle${isOpen ? ' open' : ''}`}
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
            >
              <span /><span /><span />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer */}
      <div className={`sh-drawer${isOpen ? ' open' : ''}`}>
        <div className="sh-drawer-inner">
          {NAV_LINKS.map((link, i) => (
            <a
              key={i}
              href={link.href}
              className={`sh-mobile-link${activeSection === link.href.substring(1) ? ' sh-active' : ''}`}
              onClick={(e) => { e.preventDefault(); scrollTo(link.href); }}
            >
              <span>{link.icon}</span>
              {link.name}
            </a>
          ))}
          <a
            href="#contact"
            className="sh-mobile-hire"
            onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }}
          >
            <span className="sh-dot" />
            Available for Hire
          </a>
        </div>
      </div>
    </>
  );
};

export default Header;